import { ActionButton, ActionButtonType } from "@octopusdeploy/design-system-components";
import type { MachinePolicyResource } from "@octopusdeploy/octopus-server-client";
import { CalamariUpdateBehavior, DeleteMachinesBehavior, HealthCheckType, KubernetesAgentUpdateBehavior, MachineConnectivityBehavior, MachinePackageCacheConstants, MachinePackageCacheRetentionStrategy, MachineScriptPolicyRunType, TentacleUpdateBehavior, } from "@octopusdeploy/octopus-server-client";
import { lowerFirst } from "lodash";
import pluralize from "pluralize";
import * as React from "react";
import BaseComponent from "~/components/BaseComponent";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import MarkdownDescription from "~/components/MarkdownDescription";
import { DataTable, DataTableBody, DataTableRow, DataTableRowColumn } from "~/primitiveComponents/dataDisplay/DataTable";
import { getCronExpressionDescription } from "~/utils/CronExpressions/getCronExpressionDescription";
import styles from "./style.module.less";
interface MachinePolicySummaryProps {
    machinePolicy: MachinePolicyResource;
    hideDescription: boolean;
    conciseView: boolean;
}
interface MachinePolicySummaryState {
    conciseView: boolean;
}
class MachinePolicySummary extends BaseComponent<MachinePolicySummaryProps, MachinePolicySummaryState> {
    constructor(props: MachinePolicySummaryProps) {
        super(props);
        this.state = {
            conciseView: props.conciseView,
        };
    }
    render() {
        const details = (<DataTable>
                <DataTableBody>
                    <DataTableRow>
                        <DataTableRowColumn className={styles.machinePolicySummaryLabel}>Health check interval</DataTableRowColumn>
                        <DataTableRowColumn>{this.builtHealthCheckIntervalHtml()}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowColumn className={styles.machinePolicySummaryLabel}>Health check type</DataTableRowColumn>
                        <DataTableRowColumn>{this.buildHealthCheckTypeHtml()}</DataTableRowColumn>
                    </DataTableRow>
                    {this.props.machinePolicy.MachineHealthCheckPolicy.HealthCheckType === HealthCheckType.RunScript && (<DataTableRow>
                            <DataTableRowColumn className={styles.machinePolicySummaryLabel}>PowerShell health check script</DataTableRowColumn>
                            <DataTableRowColumn>{this.buildPowerShellHealthCheckHtml()}</DataTableRowColumn>
                        </DataTableRow>)}
                    {this.props.machinePolicy.MachineHealthCheckPolicy.HealthCheckType === HealthCheckType.RunScript && (<DataTableRow>
                            <DataTableRowColumn className={styles.machinePolicySummaryLabel}>Bash health check script</DataTableRowColumn>
                            <DataTableRowColumn>{this.buildSshEndpointHealthCheckHtml()}</DataTableRowColumn>
                        </DataTableRow>)}
                    <DataTableRow>
                        <DataTableRowColumn className={styles.machinePolicySummaryLabel}>Machine connectivity</DataTableRowColumn>
                        <DataTableRowColumn>{this.buildMachineConnectivityHtml()}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowColumn className={styles.machinePolicySummaryLabel}>Calamari Machine updates</DataTableRowColumn>
                        <DataTableRowColumn>{this.buildMachineCalamariUpdatesHtml()}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowColumn className={styles.machinePolicySummaryLabel}>Tentacle Machine updates</DataTableRowColumn>
                        <DataTableRowColumn>{this.buildMachineTentacleUpdatesHtml()}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowColumn className={styles.machinePolicySummaryLabel}>Kubernetes Agent updates</DataTableRowColumn>
                        <DataTableRowColumn>{this.buildKubernetesAgentUpdatesHtml(this.props.machinePolicy)}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowColumn className={styles.machinePolicySummaryLabel}>Clean up</DataTableRowColumn>
                        <DataTableRowColumn>{this.buildCleanUpOfflineHtml()}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowColumn className={styles.machinePolicySummaryLabel}>Recover from Communication Errors with Tentacle</DataTableRowColumn>
                        <DataTableRowColumn>{this.buildTransientNetworkErrorRecoveryHtml()}</DataTableRowColumn>
                    </DataTableRow>
                    {isFeatureToggleEnabled("configurable-package-cache-retention") && (<DataTableRow>
                            <DataTableRowColumn className={styles.machinePolicySummaryLabel}>Package cache retention policy</DataTableRowColumn>
                            <DataTableRowColumn>{this.buildMachinePackageCachePolicyHtml()}</DataTableRowColumn>
                        </DataTableRow>)}
                </DataTableBody>
            </DataTable>);
        const showDetailsButton = <ActionButton type={ActionButtonType.Ternary} label="Show details" onClick={() => this.setState({ conciseView: false })}/>;
        return (<div className={styles.machinePolicySummary}>
                {!this.props.hideDescription && this.buildMachinePolicyDescriptionHtml()}
                {this.state.conciseView ? showDetailsButton : details}
            </div>);
    }
    private parseTimeSpan(timeSpan: string) {
        const timeSpanParts = timeSpan.split(":");
        let days = 0;
        let hours = 0;
        let mins = 0;
        const tsPart1 = timeSpanParts[0];
        if (tsPart1.indexOf(".") !== -1) {
            // days included
            const daysAndHours = tsPart1.split(".");
            days = parseInt(daysAndHours[0], 10);
            hours = parseInt(daysAndHours[1], 10);
            mins = parseInt(timeSpanParts[1], 10);
        }
        else {
            // no days
            hours = parseInt(timeSpanParts[0], 10);
            mins = parseInt(timeSpanParts[1], 10);
        }
        return {
            Days: days,
            Hours: hours,
            Minutes: mins,
        };
    }
    private buildMachinePolicyDescriptionHtml() {
        const policy = this.props.machinePolicy;
        return (<div className={styles.description}>
                <MarkdownDescription markup={policy.Description}/>
            </div>);
    }
    private builtHealthCheckIntervalHtml() {
        const policy = this.props.machinePolicy;
        try {
            if (policy.MachineHealthCheckPolicy.HealthCheckCron) {
                return "Automatically performs health checks " + lowerFirst(getCronExpressionDescription(policy.MachineHealthCheckPolicy.HealthCheckCron));
            }
            else if (policy.MachineHealthCheckPolicy.HealthCheckInterval) {
                let healthCheckIntervalHtml = "Automatically performs health checks every";
                const healthCheckInterval = this.parseTimeSpan(policy.MachineHealthCheckPolicy.HealthCheckInterval);
                if (healthCheckInterval.Days > 0) {
                    healthCheckIntervalHtml += " " + pluralize("day", healthCheckInterval.Days, true);
                }
                if (healthCheckInterval.Hours > 0) {
                    healthCheckIntervalHtml += " " + pluralize("hour", healthCheckInterval.Hours, true);
                }
                if (healthCheckInterval.Minutes > 0 || healthCheckInterval.Hours === 0) {
                    healthCheckIntervalHtml += " " + pluralize("minute", healthCheckInterval.Minutes, true);
                }
                return healthCheckIntervalHtml;
            }
            else {
                return "Never automatically performs health checks";
            }
        }
        catch {
            return "";
        }
    }
    private buildHealthCheckTypeHtml() {
        const policy = this.props.machinePolicy;
        let healthCheckTypeHtml = "";
        if (policy.MachineHealthCheckPolicy.HealthCheckType === HealthCheckType.RunScript) {
            healthCheckTypeHtml = "Run health check scripts";
        }
        else if (policy.MachineHealthCheckPolicy.HealthCheckType === HealthCheckType.OnlyConnectivity) {
            healthCheckTypeHtml = "Only perform connection test";
        }
        return healthCheckTypeHtml;
    }
    private buildPowerShellHealthCheckHtml() {
        const policy = this.props.machinePolicy;
        let tentacleEndpointHealthCheckHtml = "";
        if (policy.MachineHealthCheckPolicy.PowerShellHealthCheckPolicy.RunType === MachineScriptPolicyRunType.InheritFromDefault) {
            tentacleEndpointHealthCheckHtml = "Inherits from the default machine policy";
        }
        else if (policy.MachineHealthCheckPolicy.PowerShellHealthCheckPolicy.RunType === MachineScriptPolicyRunType.Inline) {
            tentacleEndpointHealthCheckHtml = "Uses custom script";
        }
        return tentacleEndpointHealthCheckHtml;
    }
    private buildSshEndpointHealthCheckHtml() {
        const policy = this.props.machinePolicy;
        let sshEndpointHealthCheckHtml = "";
        if (policy.MachineHealthCheckPolicy.BashHealthCheckPolicy.RunType === MachineScriptPolicyRunType.InheritFromDefault) {
            sshEndpointHealthCheckHtml = "Inherits from the default machine policy";
        }
        else if (policy.MachineHealthCheckPolicy.BashHealthCheckPolicy.RunType === MachineScriptPolicyRunType.Inline) {
            sshEndpointHealthCheckHtml = "Uses custom script";
        }
        return sshEndpointHealthCheckHtml;
    }
    private buildMachineConnectivityHtml() {
        const policy = this.props.machinePolicy;
        let machineConnectivityHtml = "";
        if (policy.MachineConnectivityPolicy.MachineConnectivityBehavior === MachineConnectivityBehavior.ExpectedToBeOnline) {
            machineConnectivityHtml = "Fails when a deployment target is unavailable";
        }
        else if (policy.MachineConnectivityPolicy.MachineConnectivityBehavior === MachineConnectivityBehavior.MayBeOfflineAndCanBeSkipped) {
            machineConnectivityHtml = "Ignore unavailable deployment targets";
        }
        return machineConnectivityHtml;
    }
    private buildMachineCalamariUpdatesHtml() {
        const policy = this.props.machinePolicy;
        let machineCalamariUpdatesHtml = "";
        if (policy.MachineUpdatePolicy.CalamariUpdateBehavior === CalamariUpdateBehavior.UpdateOnDeployment) {
            machineCalamariUpdatesHtml = "Automatically update when a deployment target is involved in a deployment";
        }
        else if (policy.MachineUpdatePolicy.CalamariUpdateBehavior === CalamariUpdateBehavior.UpdateOnNewMachine) {
            machineCalamariUpdatesHtml = "Automatically update the first time a deployment target comes online and then when it is involved in a deployment";
        }
        else if (policy.MachineUpdatePolicy.CalamariUpdateBehavior === CalamariUpdateBehavior.UpdateAlways) {
            machineCalamariUpdatesHtml = "Always keep up to date";
        }
        return machineCalamariUpdatesHtml;
    }
    private buildMachineTentacleUpdatesHtml() {
        const policy = this.props.machinePolicy;
        let machineTentacleUpdatesHtml = "";
        if (policy.MachineUpdatePolicy.TentacleUpdateBehavior === TentacleUpdateBehavior.NeverUpdate) {
            machineTentacleUpdatesHtml = "Manually - from Deployment Targets";
        }
        else if (policy.MachineUpdatePolicy.TentacleUpdateBehavior === TentacleUpdateBehavior.Update) {
            machineTentacleUpdatesHtml = "Automatically";
        }
        return machineTentacleUpdatesHtml;
    }
    private buildKubernetesAgentUpdatesHtml(policy: MachinePolicyResource) {
        switch (policy.MachineUpdatePolicy.KubernetesAgentUpdateBehavior) {
            case KubernetesAgentUpdateBehavior.NeverUpdate:
                return "Manually - from Deployment Targets";
            case KubernetesAgentUpdateBehavior.Update:
                return "Automatically";
            case KubernetesAgentUpdateBehavior.Block:
                return "Blocked";
            default:
                return "";
        }
    }
    private buildCleanUpOfflineHtml() {
        const policy = this.props.machinePolicy;
        let cleaningUpOfflineHtml = "";
        if (policy.MachineCleanupPolicy.DeleteMachinesBehavior === DeleteMachinesBehavior.DoNotDelete) {
            cleaningUpOfflineHtml = "Does not delete deployment targets automatically";
        }
        else if (policy.MachineCleanupPolicy.DeleteMachinesBehavior === DeleteMachinesBehavior.DeleteUnavailableMachines) {
            const deleteMachinesElapsedTimeSpan = this.parseTimeSpan(policy.MachineCleanupPolicy.DeleteMachinesElapsedTimeSpan);
            let deleteMachinesElapsedTimeSpanHtml = "Automatically delete unavailable deployment targets after";
            if (deleteMachinesElapsedTimeSpan.Days > 0) {
                deleteMachinesElapsedTimeSpanHtml += " " + pluralize("day", deleteMachinesElapsedTimeSpan.Days, true);
            }
            if (deleteMachinesElapsedTimeSpan.Hours > 0) {
                deleteMachinesElapsedTimeSpanHtml += " " + pluralize("hour", deleteMachinesElapsedTimeSpan.Hours, true);
            }
            if (deleteMachinesElapsedTimeSpan.Minutes > 0 || deleteMachinesElapsedTimeSpan.Hours === 0) {
                deleteMachinesElapsedTimeSpanHtml += " " + pluralize("minute", deleteMachinesElapsedTimeSpan.Minutes, true);
            }
            cleaningUpOfflineHtml = deleteMachinesElapsedTimeSpanHtml;
        }
        return cleaningUpOfflineHtml;
    }
    private buildMachinePackageCachePolicyHtml() {
        const policy = this.props.machinePolicy.MachinePackageCacheRetentionPolicy;
        switch (policy.Strategy) {
            case MachinePackageCacheRetentionStrategy.Quantities:
                const packageVersionMsg = pluralize("version", policy.QuantityOfVersionsToKeep, true);
                switch (policy.QuantityOfPackagesToKeep) {
                    case MachinePackageCacheConstants.KeepAll:
                        return `Keep ${packageVersionMsg} from all packages used`;
                    default:
                        return `Keep ${packageVersionMsg} from ${pluralize("package", policy.QuantityOfPackagesToKeep, true)}`;
                }
            default:
                return "Let Octopus decide";
        }
    }
    private buildTransientNetworkErrorRecoveryHtml() {
        const policy = this.props.machinePolicy;
        let machineTentacleUpdatesHtml = "";
        if (policy.MachineRpcCallRetryPolicy.Enabled) {
            machineTentacleUpdatesHtml = "Enabled";
        }
        else {
            machineTentacleUpdatesHtml = "Disabled";
        }
        return machineTentacleUpdatesHtml;
    }
    static displayName = "MachinePolicySummary";
}
export default MachinePolicySummary;
